import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import DropdownButton from './WorkflowDropdownButton';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import RestartIcon from '@mui/icons-material/SettingsBackupRestore';
import ReplayIcon from '@mui/icons-material/Replay';
import ResumeIcon from '@mui/icons-material/PlayArrow';
import FlareIcon from '@mui/icons-material/Flare';
import { useTranslation } from 'react-i18next';

export default function WorkflowActions({ execution, actions, hasRightToEditWorkflow }) {
  const { workflowId, workflowDefinition } = execution;
  const { restartable } = workflowDefinition;
  const { t } = useTranslation();
  const [, setReload] = useState(false);
  const isFailedTask = task => task.status === 'FAILED';

  useEffect(() => {
    setReload(r => !r);
  }, [execution]);

  if (execution.status === 'COMPLETED') {
    const options = [];
    if (restartable) {
      options.push({
        label: (
          <>
            <RestartIcon sx={{ mr: 1 }} fontSize='small' />
            {t('Workflow.RestartWithCurrentDefinition')}
          </>
        ),
        handler: () => actions.restartWorkflow(workflowId)
      });

      options.push({
        label: (
          <>
            <FlareIcon sx={{ mr: 1 }} fontSize='small' />
            {t('Workflow.RestartWithLatestDefinition')}
          </>
        ),
        handler: () => actions.restartWorkflowWithLatestDefinition(workflowId)
      });
    }

    return (
      <DropdownButton disabled={!hasRightToEditWorkflow} options={options}>
        {t('Workflow.Actions')}
      </DropdownButton>
    );
  } else if (execution.status === 'RUNNING') {
    return (
      <DropdownButton
        disabled={!hasRightToEditWorkflow}
        options={[
          {
            label: (
              <>
                <StopIcon sx={{ mr: 1, color: 'red' }} fontSize='small' />
                <span style={{ color: 'red' }}>Terminate</span>
              </>
            ),
            handler: () => actions.terminateWorkflow(workflowId)
          },
          {
            label: (
              <>
                <PauseIcon sx={{ mr: 1 }} fontSize='small' />
                {t('Workflow.Pause')}
              </>
            ),
            handler: () => actions.pauseWorkflow(workflowId)
          }
        ]}
      >
        {t('Workflow.Actions')}
      </DropdownButton>
    );
  } else if (execution.status === 'PAUSED') {
    return (
      <Button onClick={() => actions.resumeWorkflow(workflowId)}>
        <ResumeIcon />
        {t('Workflow.Resume')}
      </Button>
    );
  } else {
    // FAILED, TIMED_OUT, TERMINATED
    const options = [];
    if (restartable) {
      options.push({
        label: (
          <>
            <RestartIcon sx={{ mr: 1 }} fontSize='small' />
            {t('Workflow.RestartWithCurrentDefinition')}
          </>
        ),
        handler: () => actions.restartWorkflow(workflowId)
      });

      options.push({
        label: (
          <>
            <FlareIcon sx={{ mr: 1 }} fontSize='small' />
            {t('Workflow.RestartWithLatestDefinition')}
          </>
        ),
        handler: () => actions.restartWorkflowWithLatestDefinition(workflowId)
      });
    }

    options.push({
      label: (
        <>
          <ReplayIcon sx={{ mr: 1 }} fontSize='small' />
          {t('Workflow.Retry')}
        </>
      ),
      handler: () => actions.retryWorkflow(workflowId)
    });

    if (
      (execution.status === 'FAILED' || execution.status === 'TIMED_OUT') &&
      execution.tasks.find(task => task.workflowTask.type === 'SUB_WORKFLOW' && isFailedTask(task.status))
    ) {
      options.push({
        label: (
          <>
            <ReplayIcon sx={{ mr: 1 }} fontSize='small' />
            {t('Workflow.RetryResumeSubflow')}
          </>
        ),
        handler: () => actions.retryWorkflow(workflowId)
      });
    }

    return (
      <DropdownButton disabled={!hasRightToEditWorkflow} options={options}>
        {t('Workflow.Actions')}
      </DropdownButton>
    );
  }
}

WorkflowActions.propTypes = {
  execution: PropTypes.object,
  triggerReload: PropTypes.func,
  actions: PropTypes.object,
  hasRightToEditWorkflow: PropTypes.bool
};
